import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { ActivityFeed } from '../components/ActivityFeed';
import { DeliverableTracker } from '../components/DeliverableTracker';
import { Button } from '../components/ui/button';
import { Briefcase, ChevronLeft, Layers, ListChecks, Users, MessageCircle, Activity as ActivityIcon, IndianRupee, Calendar, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isManagerRole } from '../lib/roles';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageColors = {
  pitched: '#0055FF', outreach: '#9B6EFF', locked: '#00C972', live: '#FF451A', completed: '#666666', declined: '#FF3B30',
};

const TABS = [
  { value: 'overview', label: 'Overview', Icon: Layers },
  { value: 'deliverables', label: 'Deliverables', Icon: ListChecks },
  { value: 'approvals', label: 'Approvals', Icon: MessageCircle },
  { value: 'activity', label: 'Activity', Icon: ActivityIcon },
  { value: 'creator', label: 'Creator', Icon: Users },
];

export const CampaignWorkspace = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isManager = isManagerRole(user);
  const isTalentManager = user?.role === 'talent_manager';
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  // Role-aware "back" target: TMs go to Opportunities (their new home),
  // everyone else returns to the legacy /campaigns kanban.
  const backHref = isTalentManager ? '/talent/opportunities' : '/campaigns';
  const backLabel = isTalentManager ? 'Back to Opportunities' : 'Back to Campaigns';

  const fetchWorkspace = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/campaigns/${id}/workspace`, { withCredentials: true });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWorkspace(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return (
      <div className="flex"><Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-20"><div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen"><Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-12 text-center">
            <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">Campaign not found</h3>
            <p className="text-[#6B6B6B] dark:text-zinc-400 mb-5">You may not have access to this workspace.</p>
            <Link to="/campaigns" className="text-[#FF451A] font-medium">← Back to Campaigns</Link>
          </div>
        </div>
      </div>
    );
  }

  const camp = data.campaign;
  const summary = data.deliverables_summary || { total: 0, by_state: {} };
  const completedPct = summary.total > 0 ? Math.round(((summary.by_state.completed || 0) / summary.total) * 100) : 0;
  const isCompleted = camp.stage === 'completed';
  const canComplete = isManager && !isCompleted;
  const allDeliverablesDone = summary.total > 0 && (summary.by_state.completed || 0) === summary.total;

  const handleComplete = async () => {
    if (!allDeliverablesDone) {
      const proceed = window.confirm(
        `Only ${summary.by_state.completed || 0} of ${summary.total} deliverables are marked complete. Finish the campaign anyway and notify the brand?`
      );
      if (!proceed) return;
    } else if (!window.confirm('Mark this campaign as completed and notify the brand?')) {
      return;
    }
    setCompleting(true);
    try {
      await axios.patch(`${API_URL}/api/campaigns/${id}`, { stage: 'completed' }, { withCredentials: true });
      toast.success('Campaign completed · Brand notified');
      fetchWorkspace();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete campaign');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 bg-white dark:bg-[#0A0B0D]" data-testid="campaign-workspace-page">
        <div className="max-w-7xl mx-auto p-8">
          {/* Breadcrumb */}
          <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF451A] mb-4" data-testid="workspace-back-link">
            <ChevronLeft size={14} /> {backLabel}
          </Link>

          {/* Header */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 mb-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#FF451A]/10 flex items-center justify-center text-[#FF451A] shrink-0">
                    <Briefcase size={18} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-[#0F0F0F] dark:text-white truncate">{camp.name}</h1>
                    <p className="text-sm text-[#6B6B6B] dark:text-zinc-400">{camp.brand}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 self-start flex-wrap">
                <span
                  className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
                  style={{ backgroundColor: `${stageColors[camp.stage] || '#999'}15`, color: stageColors[camp.stage] || '#999' }}
                  data-testid="workspace-stage"
                >
                  {camp.stage}
                </span>
                {canComplete && (
                  <Button
                    onClick={handleComplete}
                    disabled={completing}
                    className="bg-[#00C972] hover:bg-[#00B265] text-black rounded-full px-5 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                    data-testid="workspace-complete-button"
                    title={allDeliverablesDone ? 'All deliverables completed — wrap this up' : 'Some deliverables are still pending — you can still complete the campaign'}
                  >
                    <CheckCircle2 size={16} />
                    {completing ? 'Completing…' : 'Complete & Notify Brand'}
                  </Button>
                )}
                {isCompleted && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#00C972]/15 text-[#00C972] border border-[#00C972]/30">
                    <CheckCircle2 size={12} /> Completed
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
              <Stat icon={IndianRupee} label="Budget" value={`₹${(camp.budget || 0).toLocaleString()}`} />
              <Stat icon={Users} label="Creator" value={camp.assigned_creator_name || '—'} />
              <Stat icon={Users} label="Manager" value={camp.manager_name || '—'} />
              <Stat icon={Calendar} label="Deadline" value={camp.deadline ? new Date(camp.deadline).toLocaleDateString() : '—'} />
              <Stat icon={ListChecks} label="Deliverables" value={`${summary.by_state.completed || 0}/${summary.total} (${completedPct}%)`} />
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl overflow-hidden">
            <div className="flex border-b border-black/[0.08] dark:border-white/5 overflow-x-auto">
              {TABS.map((t) => {
                const I = t.Icon;
                const active = tab === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setTab(t.value)}
                    className={`px-5 py-3 flex items-center gap-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      active ? 'border-[#FF451A] text-[#FF451A]' : 'border-transparent text-[#6B6B6B] dark:text-zinc-400 hover:text-white hover:bg-white/[0.03]'
                    }`}
                    data-testid={`workspace-tab-${t.value}`}
                  >
                    <I size={14} /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {tab === 'overview' && <OverviewTab data={data} summary={summary} completedPct={completedPct} />}
              {tab === 'deliverables' && (
                <DeliverableTracker
                  campaignId={id}
                  creatorName={camp.assigned_creator_name}
                  canCreate={isManager}
                  onChange={fetchWorkspace}
                />
              )}
              {tab === 'approvals' && (
                <DeliverableTracker
                  campaignId={id}
                  creatorName={camp.assigned_creator_name}
                  canCreate={false}
                  focusApprovals
                  onChange={fetchWorkspace}
                />
              )}
              {tab === 'activity' && (
                <ActivityFeed campaignId={id} limit={50} title="Campaign Activity" emptyText="No events yet. Activity appears as soon as people interact." />
              )}
              {tab === 'creator' && (
                <CreatorTab creatorId={camp.assigned_creator_id} creatorName={camp.assigned_creator_name} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1">
      <Icon size={11} /> {label}
    </div>
    <div className="font-semibold text-[#0F0F0F] dark:text-white text-sm truncate">{value}</div>
  </div>
);

const OverviewTab = ({ data, summary, completedPct }) => {
  const stagesOrder = ['requested', 'draft_submitted', 'revision_requested', 'approved', 'scheduled', 'posted', 'insights_pending', 'completed'];
  const camp = data.campaign;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-[#0F0F0F] dark:text-white mb-3 flex items-center gap-2"><Layers size={14} /> Brief</h3>
          <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 leading-relaxed bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-4 whitespace-pre-line">
            {data.brand_info?.brief || camp.notes || 'No brief provided.'}
          </p>
          {data.brand_info?.platforms?.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2">Platforms</div>
              <div className="flex flex-wrap gap-1.5">
                {data.brand_info.platforms.map((p) => (
                  <span key={p} className="px-2 py-1 bg-[#FF451A]/10 text-[#FF451A] text-xs rounded-full font-medium">{p}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-[#0F0F0F] dark:text-white mb-3 flex items-center gap-2"><ListChecks size={14} /> Deliverable Pipeline</h3>
          <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-4">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm text-[#6B6B6B] dark:text-zinc-400">Completion</span>
              <span className="text-2xl font-bold text-[#FF451A]">{completedPct}%</span>
            </div>
            <div className="w-full h-2 bg-white dark:bg-[#0A0B0D] rounded-full overflow-hidden mb-4">
              <div className="h-full bg-[#FF451A] rounded-full transition-all" style={{ width: `${completedPct}%` }}></div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
              {stagesOrder.map((s) => (
                <div key={s} className="flex items-center justify-between">
                  <span className="text-[#6B6B6B] dark:text-zinc-400 capitalize">{s.replace('_', ' ')}</span>
                  <span className="font-semibold text-[#0F0F0F] dark:text-white">{summary.by_state?.[s] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ActivityFeed campaignId={camp.id} limit={10} title="Latest activity" emptyText="No activity yet." />
    </div>
  );
};

const CreatorTab = ({ creatorId, creatorName }) => {
  if (!creatorId) return <p className="text-sm text-[#6B6B6B] dark:text-zinc-500">No creator assigned yet.</p>;
  return (
    <div className="bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-xl p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF451A] to-[#0055FF] flex items-center justify-center text-[#0F0F0F] dark:text-white font-bold">
        {creatorName?.[0]?.toUpperCase() || 'C'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#0F0F0F] dark:text-white">{creatorName}</div>
        <div className="text-xs text-[#6B6B6B] dark:text-zinc-400">Open full profile to see audience insights, history & AI suitability tags.</div>
      </div>
      <Link
        to={`/creators/${creatorId}`}
        className="bg-white dark:bg-[#0A0B0D] text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-black flex items-center gap-1"
        data-testid="open-creator-profile-link"
      >
        Open Profile <ArrowRight size={14} />
      </Link>
    </div>
  );
};
